import { useEffect } from "react";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

export default function MembershipSEO() {
  useEffect(() => {
    document.title =
      "Crown Membership Plans — Basic $5/mo & Gold $18/mo | Crown Platform";

    let desc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!desc) {
      desc = document.createElement("meta");
      desc.name = "description";
      document.head.appendChild(desc);
    }
    desc.content =
      "Join Crown Platform with Basic ($5/mo) or Gold ($18/mo) membership. Post unlimited jobs, ads & factory products. Priority placement, verified badge & analytics. Cancel anytime.";

    let keywords = document.querySelector('meta[name="keywords"]') as HTMLMetaElement | null;
    if (!keywords) {
      keywords = document.createElement("meta");
      keywords.name = "keywords";
      document.head.appendChild(keywords);
    }
    keywords.content =
      "crown membership, crown platform plans, basic membership, gold membership, jobs ads subscription, business marketplace membership";

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = `${SITE_URL}/membership`;

    const existingScript = document.getElementById("membership-jsonld");
    if (existingScript) existingScript.remove();

    const script = document.createElement("script");
    script.id = "membership-jsonld";
    script.type = "application/ld+json";
    script.text = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": `${SITE_URL}/membership#webpage`,
        url: `${SITE_URL}/membership`,
        name: "Crown Membership Plans — Basic $5/mo & Gold $18/mo",
        description:
          "Choose your Crown Platform membership. Basic $5/mo or Gold $18/mo. Post jobs, ads, factory products and more.",
        inLanguage: "en-US",
        dateModified: "2026-04-10",
        isPartOf: { "@id": `${SITE_URL}/#website` },
        about: { "@id": `${SITE_URL}/#organization` },
        breadcrumb: {
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: SITE_URL,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Membership",
              item: `${SITE_URL}/membership`,
            },
          ],
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "Product",
        "@id": `${SITE_URL}/membership#basic`,
        name: "Crown Platform Basic Membership",
        description:
          "Post up to 3 job listings and 3 ads per month, view buyer contacts, list up to 5 factory products, basic seller profile, email support.",
        brand: {
          "@type": "Brand",
          name: "Crown Platform",
        },
        url: `${SITE_URL}/membership`,
        offers: {
          "@type": "Offer",
          name: "Basic Member",
          price: "5.00",
          priceCurrency: "USD",
          priceSpecification: {
            "@type": "UnitPriceSpecification",
            price: "5.00",
            priceCurrency: "USD",
            unitCode: "MON",
            billingDuration: 1,
            billingIncrement: 1,
          },
          availability: "https://schema.org/InStock",
          url: `${SITE_URL}/membership`,
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "Product",
        "@id": `${SITE_URL}/membership#gold`,
        name: "Crown Platform Gold Membership",
        description:
          "Unlimited job listings, unlimited ads, all buyer contacts, unlimited factory products, priority placement, Gold verified badge, analytics dashboard, dropshipping access, social media star commission, 24/7 priority support.",
        brand: {
          "@type": "Brand",
          name: "Crown Platform",
        },
        url: `${SITE_URL}/membership`,
        offers: {
          "@type": "Offer",
          name: "Gold Member",
          price: "18.00",
          priceCurrency: "USD",
          priceSpecification: {
            "@type": "UnitPriceSpecification",
            price: "18.00",
            priceCurrency: "USD",
            unitCode: "MON",
            billingDuration: 1,
            billingIncrement: 1,
          },
          availability: "https://schema.org/InStock",
          url: `${SITE_URL}/membership`,
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "Crown Platform Membership Plans",
        description: "Choose from Basic or Gold membership plans on Crown Platform.",
        numberOfItems: 2,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            item: { "@id": `${SITE_URL}/membership#basic` },
          },
          {
            "@type": "ListItem",
            position: 2,
            item: { "@id": `${SITE_URL}/membership#gold` },
          },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "Can I cancel my Crown membership anytime?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, you can cancel your membership at any time. Your access continues until the end of the billing period.",
            },
          },
          {
            "@type": "Question",
            name: "Do buyers need to pay for Crown Platform?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "No! Buyers always post their purchasing requirements completely FREE. Only sellers, factories, advertisers, and job posters need a membership.",
            },
          },
          {
            "@type": "Question",
            name: "What is the difference between Basic and Gold membership?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Basic ($5/mo) gives you limited posts and access to buyer contacts. Gold ($18/mo) gives you unlimited posts, priority placement, verified badge, analytics, dropshipping access, and all premium features.",
            },
          },
          {
            "@type": "Question",
            name: "Is my payment secure on Crown Platform?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes. All payments are processed with 256-bit SSL encryption. We never store your card details on our servers.",
            },
          },
          {
            "@type": "Question",
            name: "Can I upgrade from Basic to Gold?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, you can upgrade at any time. You will only be charged the difference for the remaining billing period.",
            },
          },
          {
            "@type": "Question",
            name: "Does Gold membership include dropshipping access?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes! Gold members get full access to the Crown Platform Warehouse Dropshipping system, including supplier product uploads, seller storefront management, and social media influencer commission tracking.",
            },
          },
        ],
      },
    ]);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById("membership-jsonld");
      if (s) s.remove();
    };
  }, []);

  return null;
}
